import { useEffect, useRef } from 'react';
import { notify } from '../../../utils/notification';
import { DIVINE_SENSE_EFFECT_ID, DIVINE_SENSE_RANGE_FEET, divineSenseCreatureType } from '../../../shared/rules/paladin.mjs';
import { minimumFootprintDistanceFeet } from '../utils/pushGeometryCore.mjs';

/** Celestials, Fiends and Undead on the map within 60 feet of the Paladin, as `{ id, name, type }`. */
export const senseDivineCreatures = ({ characterId, spatialContext, size }) => {
  const tokens = spatialContext?.tokens || {};
  const source = tokens[characterId];
  if (!source || !spatialContext?.map) return [];
  return Object.entries(spatialContext.entities || {}).flatMap(([id, entity]) => {
    const type = id === String(characterId) ? null : divineSenseCreatureType(entity);
    if (!type || !tokens[id]) return [];
    const distance = minimumFootprintDistanceFeet({ sourceToken: source, targetToken: tokens[id], sourceSize: size, targetSize: entity.size, map: spatialContext.map });
    return distance !== null && distance <= DIVINE_SENSE_RANGE_FEET ? [{ id, name: entity.label || entity.name || id, type }] : [];
  });
};
const describe = creatures => (creatures.length ? creatures.map(creature => `${creature.name} (${creature.type})`).join(', ') : 'no Celestials, Fiends or Undead within 60 feet');

/**
 * Divine Sense (Channel Divinity): when it starts, tell the Paladin which Celestials, Fiends and Undead are within
 * 60 feet; while it lasts, announce each one that comes into range. An effect already running on load stays quiet.
 */
export default function useDivineSenseNotice({ form, characterId, combatState, spatialContext }) {
  const seen = useRef(undefined);
  const active = (combatState?.activeEffects || []).some(effect => effect?.definitionId === DIVINE_SENSE_EFFECT_ID && String(effect.sourceCombatantId) === String(characterId));
  const sensed = active ? senseDivineCreatures({ characterId: String(characterId), spatialContext, size: form?.temporarySize ?? form?.size }) : [];
  const key = sensed.map(creature => creature.id).sort().join('|');
  useEffect(() => {
    const previous = seen.current;
    seen.current = active ? new Set(sensed.map(creature => creature.id)) : null;
    if (previous === undefined || !active) return;
    if (previous === null) { notify(`Divine Sense · ${describe(sensed)}.`, 'info'); return; }
    const arrived = sensed.filter(creature => !previous.has(creature.id));
    if (arrived.length) notify(`Divine Sense · ${describe(arrived)} now within 60 feet.`, 'info');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, key]);
}
